import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { HiOutlineX } from "react-icons/hi";

import DateTimePicker from "../../components/DateTimePicker";
import FilePreviewModal, {
  detectPreviewKind,
  type FilePreviewKind,
} from "components/FilePreviewModal";
import uploadDropzoneGraphic from "assets/svg/upload-modal-dropzone-graphic.svg";

import {
  bulkUpdateFollowUpTmMEntries,
  getFollowUpTmMContext,
  saveFollowUpTmMDocument,
  updateFollowUpTmMEntry,
  type FollowUpTmMApplication,
} from "../../api/followUpFiling";
import { generateUploadPresignedUrls } from "../../api/discussion";
import { parseFilingApiDateString, toLocalYmd } from "../../utils/filingApiDates";
import { downloadFileFromUrlWithFallback } from "../../utils/downloadFileFromUrl";

export const FOLLOW_UP_FILE_TM_M_FORM_ID = "follow-up-file-tm-m-form";

const ACCEPTED_TYPES = ".jpg,.jpeg,.png,.pdf,.mp4";
const ACCEPTED_HELP = "JPEG, PNG, PDF, and MP4 formats, up to 50MB";
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;
const TM_M_UPLOAD_FOLDER_PREFIX = "follow-up/tm-m";

const LABEL_STYLE =
  "text-xs font-normal leading-snug text-[#222222] w-full flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5";
const LABEL_FEATURES = { fontFeatureSettings: "'ss01' on, 'cv01' on" } as const;

type ApplicationRowState = FollowUpTmMApplication & {
  /** Optimistic local file name shown while uploading. */
  pendingFilename?: string | null;
};

type PreviewState = {
  url: string;
  name: string;
  kind: FilePreviewKind;
  revokeOnClose?: boolean;
};

type FollowUpFileTmMViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

function toIsoDate(value: string | null | undefined): string | undefined {
  const d = parseFilingApiDateString(value ?? null);
  if (!d) return undefined;
  return toLocalYmd(d);
}

function dateValue(value: string | null | undefined): Date | null {
  return parseFilingApiDateString(value ?? null);
}

export default function FollowUpFileTmMView({
  leadId,
  onSuccess,
}: FollowUpFileTmMViewProps) {
  const [applications, setApplications] = useState<ApplicationRowState[]>([]);
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [uploadingIds, setUploadingIds] = useState<Record<string, boolean>>({});
  const [preview, setPreview] = useState<PreviewState | null>(null);

  const autoSaveTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>(
    {}
  );

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      setApplications([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getFollowUpTmMContext(leadId)
      .then((data) => {
        if (cancelled) return;
        setApplications(data.applications ?? []);
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(
          err instanceof Error ? err.message : "Failed to load File TM-M data"
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  useEffect(() => {
    return () => {
      Object.values(autoSaveTimers.current).forEach((t) => clearTimeout(t));
      autoSaveTimers.current = {};
    };
  }, []);

  const totalCount = applications.length;
  const filedCount = useMemo(
    () =>
      applications.filter(
        (app) =>
          (app.dateOfFilingTmM ?? "").trim().length > 0 &&
          app.tmMDocument != null
      ).length,
    [applications]
  );

  const scheduleAutoSave = (applicationId: string, isoDate: string | null) => {
    if (!leadId) return;
    const key = `date-${applicationId}`;
    const existing = autoSaveTimers.current[key];
    if (existing) clearTimeout(existing);
    autoSaveTimers.current[key] = setTimeout(() => {
      updateFollowUpTmMEntry(leadId, applicationId, {
        dateOfFilingTmM: isoDate ?? undefined,
      })
        .then((data) => {
          setApplications((prev) =>
            mergeApplications(prev, data.applications)
          );
        })
        .catch(() => {
          // Auto-save errors are non-blocking; final submit will surface real issues.
        });
    }, 600);
  };

  const handleDateChange = (index: number, next: Date | null) => {
    setApplications((prev) => {
      const arr = [...prev];
      const row = arr[index];
      if (!row) return prev;
      const iso = next ? toLocalYmd(next) : null;
      arr[index] = { ...row, dateOfFilingTmM: iso };
      scheduleAutoSave(row.applicationId, iso);
      return arr;
    });
  };

  const setRowUploading = (applicationId: string, value: boolean) => {
    setUploadingIds((prev) => {
      if (value) return { ...prev, [applicationId]: true };
      const next = { ...prev };
      delete next[applicationId];
      return next;
    });
  };

  const uploadFileForRow = async (index: number, file: File) => {
    if (!leadId) return;
    const row = applications[index];
    if (!row) return;
    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`${file.name} exceeds the 50MB limit`);
      return;
    }
    const applicationId = row.applicationId;
    setApplications((prev) => {
      const arr = [...prev];
      const target = arr.findIndex((a) => a.applicationId === applicationId);
      if (target === -1) return prev;
      arr[target] = { ...arr[target], pendingFilename: file.name };
      return arr;
    });
    setRowUploading(applicationId, true);
    try {
      const [presigned] = await generateUploadPresignedUrls(
        [
          {
            filename: file.name,
            mimeType: file.type || "application/octet-stream",
          },
        ],
        "private",
        `${TM_M_UPLOAD_FOLDER_PREFIX}/${leadId}`
      );
      if (!presigned?.url || !presigned?.key) {
        throw new Error("Failed to generate upload URL");
      }

      const putRes = await fetch(presigned.url, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type || "application/octet-stream",
        },
      });
      if (!putRes.ok) {
        throw new Error(`Failed to upload ${file.name}`);
      }

      const saved = await saveFollowUpTmMDocument(leadId, applicationId, {
        key: presigned.key,
        filename: file.name,
      });

      setApplications((prev) =>
        prev.map((app) =>
          app.applicationId === applicationId
            ? {
                ...app,
                pendingFilename: null,
                tmMDocument: {
                  key: saved.key,
                  filename: saved.filename,
                  url: saved.url,
                },
              }
            : app
        )
      );
      toast.success(`Uploaded ${file.name}`);
    } catch (err) {
      setApplications((prev) =>
        prev.map((app) =>
          app.applicationId === applicationId
            ? { ...app, pendingFilename: null }
            : app
        )
      );
      toast.error(err instanceof Error ? err.message : "Failed to upload file");
    } finally {
      setRowUploading(applicationId, false);
    }
  };

  const openPreview = (row: ApplicationRowState) => {
    const doc = row.tmMDocument;
    if (!doc?.url) {
      toast.error("Preview is not available yet.");
      return;
    }
    setPreview({
      url: doc.url,
      name: doc.filename,
      kind: detectPreviewKind(doc.filename),
    });
  };

  const closePreview = () => {
    if (preview?.revokeOnClose) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const handleDownload = async (row: ApplicationRowState) => {
    const doc = row.tmMDocument;
    if (!doc?.url) {
      toast.error("No file available to download.");
      return;
    }
    try {
      const result = await downloadFileFromUrlWithFallback(doc.url, doc.filename);
      if (result === "local") {
        toast.success("File saved to your device.");
      } else {
        toast.info(
          "Opened the file in a new tab. Use Save or Save as to store it locally."
        );
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to download file");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    if (applications.length === 0) {
      toast.error("No applications found. Complete the previous steps first.");
      return;
    }
    const missingDate = applications.find(
      (app) => !toIsoDate(app.dateOfFilingTmM)
    );
    if (missingDate) {
      toast.error(
        `Please select Date of Filing TM-M for TM No. ${missingDate.tmNo}.`
      );
      return;
    }
    const missingDoc = applications.find((app) => !app.tmMDocument);
    if (missingDoc) {
      toast.error(
        `Please upload TM-M document for TM No. ${missingDoc.tmNo}.`
      );
      return;
    }
    Object.values(autoSaveTimers.current).forEach((t) => clearTimeout(t));
    autoSaveTimers.current = {};
    setSaving(true);
    try {
      const items = applications.map((app) => ({
        tmNo: app.tmNo,
        dateOfFilingTmM: toIsoDate(app.dateOfFilingTmM)!,
      }));
      const data = await bulkUpdateFollowUpTmMEntries(leadId, items);
      setApplications((prev) => mergeApplications(prev, data.applications));
      toast.success("File TM-M for Follow-up saved successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access
          this form.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading File TM-M data...</p>
      </div>
    );
  }

  if (applications.length === 0) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          No applications found. Please complete Application Details from Client
          first.
        </p>
      </div>
    );
  }

  const isMultiApplication = applications.length > 1;

  return (
    <form
      id={FOLLOW_UP_FILE_TM_M_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-4 md:gap-5 w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {isMultiApplication ? (
        <>
          <p
            className="text-xs font-medium leading-5 text-[#222]"
            style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
          >
            {filedCount} of {totalCount} application{totalCount === 1 ? "" : "s"}{" "}
            ready to file.
          </p>

          <div
            className="hidden lg:grid lg:grid-cols-[160px_220px_1fr] gap-5 items-center shrink-0 text-[#222] text-sm font-medium leading-5 mb-1"
            style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
          >
            <div>TM No.</div>
            <div>Date of Filing TM-M</div>
            <div>Document</div>
          </div>

          <div className="flex flex-col gap-3">
            {applications.map((row, idx) => {
              const inputId = `follow-up-file-tm-m-input-${row.applicationId}`;
              const uploading = !!uploadingIds[row.applicationId];
              const dragging = draggingIndex === idx;
              const fileLabel =
                row.pendingFilename ?? row.tmMDocument?.filename ?? null;
              const hasFile = !!fileLabel;
              const isSaved = !!row.tmMDocument;

              return (
                <div
                  key={row.applicationId}
                  className="grid grid-cols-1 lg:grid-cols-[160px_220px_1fr] gap-3 lg:gap-5 lg:items-start w-full p-4 lg:p-0 rounded-[6px] border border-[#EEE] lg:border-0 bg-white lg:bg-transparent"
                >
                  <div className="flex flex-col gap-1 min-w-0">
                    <span className="lg:hidden text-xs font-normal text-[#222222]">
                      TM No.
                    </span>
                    <div className="flex flex-col gap-0.5 lg:min-h-[44px] lg:justify-center min-w-0">
                      <span className="text-[12px] lg:text-sm font-medium text-[#222] truncate">
                        {row.tmNo || "—"}
                      </span>
                      {row.mark ? (
                        <span className="text-[11px] lg:text-xs font-normal text-[#6D6D6D] truncate">
                          {row.mark}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex flex-col gap-1 w-full min-w-0">
                    <span className="lg:hidden text-xs font-normal text-[#222222]">
                      Date of Filing TM-M
                    </span>
                    <DateTimePicker
                      value={dateValue(row.dateOfFilingTmM)}
                      onChange={(d) => handleDateChange(idx, d)}
                      showTime={false}
                      buttonClassName="min-h-[44px] lg:min-h-0 rounded-[6px] text-[12px] lg:text-xs"
                    />
                  </div>

                  <div className="flex flex-col gap-1 w-full min-w-0">
                    <span className="lg:hidden text-xs font-normal text-[#222222]">
                      Document
                    </span>
                    {hasFile ? (
                      <div
                        className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-3 py-2 min-h-[44px] rounded-[8px] border ${
                          isSaved
                            ? "border-[#22C55E] bg-[#F0FDF4]"
                            : "border-[#EEE] bg-[#FCFCFC]"
                        }`}
                      >
                        <span
                          className={`text-xs truncate min-w-0 flex-1 ${
                            isSaved
                              ? "text-[#16A34A] font-medium"
                              : "text-[#222222]"
                          }`}
                          title={fileLabel ?? undefined}
                        >
                          {fileLabel}
                          {uploading ? " · Uploading…" : ""}
                        </span>
                        <div className="flex items-center gap-2 flex-wrap shrink-0">
                          {isSaved ? (
                            <>
                              <button
                                type="button"
                                onClick={() => openPreview(row)}
                                className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50"
                              >
                                Preview
                              </button>
                              <button
                                type="button"
                                onClick={() => void handleDownload(row)}
                                className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50"
                              >
                                Download
                              </button>
                            </>
                          ) : null}
                          <button
                            type="button"
                            aria-label={
                              isSaved ? `Replace ${fileLabel}` : "Select file"
                            }
                            disabled={uploading}
                            onClick={() =>
                              document.getElementById(inputId)?.click()
                            }
                            className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            Replace
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        className={`flex items-center justify-between gap-3 px-3 py-2 min-h-[44px] rounded-[8px] border border-dashed bg-white transition-colors ${
                          dragging
                            ? "border-[#222222] bg-[#F5F5F5]"
                            : "border-[#D8DAEB]"
                        } ${
                          uploading
                            ? "opacity-60 cursor-not-allowed"
                            : "cursor-pointer hover:bg-gray-50"
                        }`}
                        onDragOver={(e) => {
                          e.preventDefault();
                          setDraggingIndex(idx);
                        }}
                        onDragLeave={() => setDraggingIndex(null)}
                        onDrop={(e) => {
                          e.preventDefault();
                          setDraggingIndex(null);
                          if (uploading) return;
                          const file = e.dataTransfer.files?.[0];
                          if (file) void uploadFileForRow(idx, file);
                        }}
                        onClick={() => {
                          if (!uploading)
                            document.getElementById(inputId)?.click();
                        }}
                      >
                        <span className="text-xs text-[#7D7D7D] truncate min-w-0 flex-1">
                          {uploading
                            ? "Uploading…"
                            : "Drop file here or click to upload"}
                        </span>
                        <span className="shrink-0 inline-flex items-center justify-center h-8 px-3 rounded-[8px] bg-white border border-[#D5D3E0] text-xs font-medium text-[#2E3037]">
                          Select File
                        </span>
                      </div>
                    )}
                    <input
                      id={inputId}
                      type="file"
                      accept={ACCEPTED_TYPES}
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) void uploadFileForRow(idx, file);
                        e.currentTarget.value = "";
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        applications.map((row, idx) => {
          const inputId = `follow-up-file-tm-m-input-${row.applicationId}`;
          const uploading = !!uploadingIds[row.applicationId];
          const dragging = draggingIndex === idx;
          const fileLabel =
            row.pendingFilename ?? row.tmMDocument?.filename ?? null;
          const hasFile = !!fileLabel;

          return (
            <div
              key={row.applicationId}
              className="flex flex-col gap-4 md:gap-5 w-full"
            >
              <div className="flex flex-1 min-w-0 flex-col w-full gap-1.5">
                <label className={LABEL_STYLE} style={LABEL_FEATURES}>
                  <span className="min-w-0 break-words">
                    Date of Filing TM-M for Follow-up
                  </span>
                </label>
                <DateTimePicker
                  value={dateValue(row.dateOfFilingTmM)}
                  onChange={(d) => handleDateChange(idx, d)}
                  showTime={false}
                  buttonClassName="min-h-[44px] md:min-h-0 rounded-[6px] text-[12px] md:text-xs"
                />
              </div>

              <div
                className="flex flex-col gap-5 self-stretch bg-white border border-[#EEEEEE] rounded-[10px] p-5"
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                <div className="flex flex-col gap-2 self-stretch">
                  <p
                    className="font-semibold text-[#101111]"
                    style={{
                      fontSize: "14px",
                      lineHeight: "100%",
                      letterSpacing: "0.01em",
                    }}
                  >
                    Upload Documents
                  </p>
                  <p
                    className="font-normal text-[#7D7D7D]"
                    style={{
                      fontSize: "12px",
                      lineHeight: "24px",
                      letterSpacing: "0.04em",
                    }}
                  >
                    Choose a file and upload securely to proceed.
                  </p>
                </div>

                <div
                  className={`self-stretch flex flex-col sm:flex-row sm:justify-between sm:items-center gap-5 px-5 py-5 rounded-[16px] border border-dashed bg-white transition-colors cursor-pointer ${
                    dragging
                      ? "border-[#222222] bg-[#F5F5F5]"
                      : hasFile
                        ? "border-[#22C55E] bg-[#F0FDF4]"
                        : "border-[#D8DAEB]"
                  }`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDraggingIndex(idx);
                  }}
                  onDragLeave={() => setDraggingIndex(null)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDraggingIndex(null);
                    if (uploading) return;
                    const file = e.dataTransfer.files?.[0];
                    if (file) void uploadFileForRow(idx, file);
                  }}
                  onClick={() => {
                    if (uploading) return;
                    document.getElementById(inputId)?.click();
                  }}
                >
                  <div className="flex flex-row items-center gap-5 min-w-0">
                    <img
                      src={uploadDropzoneGraphic}
                      alt=""
                      width={60}
                      height={60}
                      className="flex-shrink-0"
                    />
                    <div className="flex flex-col gap-3 min-w-0">
                      {hasFile ? (
                        <>
                          <p
                            className="font-medium text-[#16A34A] truncate"
                            style={{
                              fontSize: "16px",
                              lineHeight: "100%",
                              letterSpacing: "0.01em",
                            }}
                          >
                            {fileLabel}
                          </p>
                          <p
                            className="font-normal text-[#7D7D7D]"
                            style={{ fontSize: "14px", lineHeight: "21px" }}
                          >
                            {uploading ? "Uploading…" : ACCEPTED_HELP}
                          </p>
                        </>
                      ) : (
                        <>
                          <p
                            className="font-medium text-[#101111]"
                            style={{
                              fontSize: "16px",
                              lineHeight: "100%",
                              letterSpacing: "0.01em",
                            }}
                          >
                            Drag and drop your files
                          </p>
                          <p
                            className="font-normal text-[#7D7D7D]"
                            style={{ fontSize: "14px", lineHeight: "21px" }}
                          >
                            {uploading ? "Uploading…" : ACCEPTED_HELP}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={uploading}
                    onClick={(e) => {
                      e.stopPropagation();
                      document.getElementById(inputId)?.click();
                    }}
                    className="flex-shrink-0 flex items-center justify-center h-9 px-4 rounded-[10px] bg-white border border-[#D5D3E0] capitalize text-[#2E3037] hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed self-start sm:self-auto"
                    style={{
                      boxShadow: "0px 5px 20px rgba(0, 0, 0, 0.02)",
                      fontSize: "14px",
                      fontWeight: 500,
                      lineHeight: "16px",
                      letterSpacing: "0.04em",
                    }}
                  >
                    {hasFile ? "Replace" : "Select File"}
                  </button>
                </div>

                <input
                  id={inputId}
                  type="file"
                  accept={ACCEPTED_TYPES}
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void uploadFileForRow(idx, file);
                    e.currentTarget.value = "";
                  }}
                />

                {row.tmMDocument && (
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-3 py-2 rounded-[8px] border border-[#EEE] bg-[#FCFCFC]">
                    <span className="text-xs text-[#222222] truncate min-w-0 flex-1">
                      {row.tmMDocument.filename}
                    </span>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => openPreview(row)}
                        className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50"
                      >
                        Preview
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleDownload(row)}
                        className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50"
                      >
                        Download
                      </button>
                      <button
                        type="button"
                        aria-label={`Replace ${row.tmMDocument.filename}`}
                        onClick={() =>
                          document.getElementById(inputId)?.click()
                        }
                        className="text-[#6D6D6D] hover:text-[#222] p-1"
                        title="Replace"
                      >
                        <HiOutlineX className="w-4 h-4" strokeWidth={2} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}

      <FilePreviewModal
        open={!!preview}
        onClose={closePreview}
        name={preview?.name ?? ""}
        url={preview?.url}
        kind={preview?.kind}
      />
    </form>
  );
}

function mergeApplications(
  prev: ApplicationRowState[],
  next: FollowUpTmMApplication[]
): ApplicationRowState[] {
  const prevById = new Map(prev.map((a) => [a.applicationId, a]));
  return next.map((app) => {
    const cached = prevById.get(app.applicationId);
    return {
      ...app,
      pendingFilename: cached?.pendingFilename ?? null,
    };
  });
}
